import {
  CreateAIGenerationInput,
  GenerateWithAIButtonMutation,
} from "@/content/ai/button/__generated__/GenerateWithAIButtonMutation.graphql"
import { GenerateWithAIFormState } from "@/content/ai/GenerateWithAIForm"
import useTrackAIGenerationStatus from "@/content/ai/hooks/useTrackAIGenerationStatus"
import { useActiveProduct } from "@/core/context/ActiveProductContext"
import { useGlobalDrawer } from "@/core/context/GlobalDrawerProvider"
import { useFormStore } from "@/core/form/store/FormStore"
import ROUTE_NAMES from "@/core/route/util/routeNames"
import { GlobalID } from "@/relay/RelayTypes"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import {
  OverridableDiscoButton,
  OverridableDiscoButtonChildren,
} from "@disco-ui/button/OverridableDiscoButton"
import { useQueryParamState } from "@disco-ui/tabs/DiscoQueryParamTabs"
import useDisclosure from "@utils/hook/useDisclosure"
import { TestIDProps } from "@utils/typeUtils"
import { observer } from "mobx-react-lite"
import { generatePath, useHistory } from "react-router-dom"
import { graphql } from "relay-runtime"
import GenerateWithAIModal from "../GenerateWithAIModal"
import { EXAMPLE_CURRICULUM_GENERATION_PLACEHOLDER } from "../utils/AIGenerationUtils"

interface Props extends TestIDProps {
  curriculumId: GlobalID
  children: OverridableDiscoButtonChildren
  onSuccess?: () => void
}

type CurriculumGenerationQueryParams = {
  openCurriculumGenModal?: "1"
}

function GenerateWithAIButton({
  curriculumId,
  children,
  onSuccess,
  testid = "GenerateAIButton",
}: Props) {
  const activeProduct = useActiveProduct()
  const classes = useStyles()
  const [{ openCurriculumGenModal }, setQueryParams] =
    useQueryParamState<CurriculumGenerationQueryParams>()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const history = useHistory()
  const drawer = useGlobalDrawer("aiCanvas")

  const { aiGenerationId, hasIncompleteGeneration, isGeneratingEntity } =
    useTrackAIGenerationStatus({
      entityId: curriculumId,
    })

  const form = useFormStore<
    GenerateWithAIButtonMutation,
    CreateAIGenerationInput & GenerateWithAIFormState
  >(
    graphql`
      mutation GenerateWithAIButtonMutation($input: CreateAIGenerationInput!) {
        response: createAIGeneration(input: $input) {
          node {
            status
          }
          errors {
            field
            message
          }
        }
      }
    `,
    {
      prompt: "",
      referenceUrls: [],
      referenceEmbeddingSourceIds: [],
      entityId: curriculumId,
      productId: activeProduct?.id || null,
      assetEmbeddingSourceIds: [],
      contentEmbeddingSourceIds: [],
      trainingDataEmbeddingSourceIds: [],
      uploadedAssetIds: [],
      uploadedAssets: [],
      searchWeb: true,
      communityContext: true,
    }
  )

  return (
    <>
      <OverridableDiscoButton
        testid={testid}
        onClick={handleClick}
        shouldDisplaySpinnerWithText={isGeneratingEntity}
        disabled={isGeneratingEntity}
      >
        {children}
      </OverridableDiscoButton>

      <GenerateWithAIModal
        testid={testid}
        modalContentLabel={"Curriculum Generation"}
        title={"Generate Curriculum with AI"}
        classes={{
          footer: classes.modalFooter,
          leftSideFooter: classes.modalLeftSideFooter,
        }}
        isOpen={openCurriculumGenModal === "1" || isOpen}
        onClose={handleOnClose}
        placeholder={EXAMPLE_CURRICULUM_GENERATION_PLACEHOLDER}
        form={form}
        onSubmit={handleSubmit}
        canUploadMedia
      />
    </>
  )

  function handleClick() {
    if (!hasIncompleteGeneration) {
      onOpen()
      return
    }
    // If we're on the curriculum page already, open the manually closed drawer
    const curriculumPath = generatePath(ROUTE_NAMES.PRODUCT.CURRICULUM.ROOT, {
      productSlug: activeProduct!.slug,
    })
    if (location.pathname.startsWith(curriculumPath)) {
      drawer.open({ aiGenerationId, entityId: curriculumId })
      return
    }
    // Otherwise, go to the curriculum and the drawer will auto-open
    history.push(curriculumPath)
  }

  async function handleSubmit() {
    const { contentEmbeddingSourceIds, assetEmbeddingSourceIds } = form.state

    const { didSave } = await form.submit({
      prompt: form.state.prompt,
      entityId: form.state.entityId,
      productId: form.state.productId,
      uploadedAssetIds: form.state.uploadedAssetIds,
      referenceUrls: form.state.referenceUrls,
      referenceEmbeddingSourceIds: [
        ...(contentEmbeddingSourceIds || []),
        ...(assetEmbeddingSourceIds || []),
        ...(form.state.trainingDataEmbeddingSourceIds || []),
      ],
      searchWeb: form.state.searchWeb,
      communityContext: form.state.communityContext,
    })

    if (!didSave) return

    onSuccess?.()
    handleOnClose()
  }

  function handleOnClose() {
    setQueryParams({ openCurriculumGenModal: undefined })
    onClose()
  }
}

const useStyles = makeUseStyles((theme) => ({
  modalFooter: {
    gap: theme.spacing(1.5),
  },
  modalLeftSideFooter: {
    width: "100%",
  },
}))

export default observer(GenerateWithAIButton)
